import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@aps/ui';
import { useSession } from '../app/session-context.js';
import { messages } from '../i18n/messages.js';
import {
  CROP_ASPECT_KEYS,
  MAX_ZOOM,
  clampOffset,
  clampZoom,
  coverScale,
  cropPlan,
  croppedFileName,
  frameSize,
  minZoom,
  outputType,
  type CropAspect,
  type Point,
  type Size,
} from '../lib/crop.js';

/** Ekrandagi kadrning uzun tomoni. Natija asl piksellardan olinadi, bu faqat ko'rinish. */
const FRAME_LONG_SIDE = 340;
const NUDGE = 8;
const NUDGE_FAST = 32;
const ZOOM_KEY_FACTOR = 1.1;
const ZOOM_WHEEL_SPEED = 0.002;
const OUTPUT_QUALITY = 0.92;

export interface ImageCropDialogProps {
  file: File;
  /** Ko'p faylda qaysinchisi: "2 / 5". */
  position: { index: number; total: number };
  /** Kesilgan fayl tayyor. */
  onDone: (file: File) => void;
  /** Kesmasdan asl faylni yuklash. */
  onSkip: () => void;
  /** Bu faylni yuklamaslik. */
  onCancel: () => void;
  /** Tanlash mumkin bo'lgan nisbatlar; birinchisi boshlang'ich. Standart: tovar rasmi. */
  aspects?: readonly CropAspect[];
  /** Ekrandagi kadrning uzun tomoni (piksel). Keng nisbat uchun kattaroq. */
  frameLongSide?: number;
}

const ASPECT_LABEL_KEYS = {
  square: 'cropAspectSquare',
  landscape: 'cropAspectLandscape',
  portrait: 'cropAspectPortrait',
  banner: 'cropAspectBanner',
} as const satisfies Record<CropAspect, string>;

/**
 * Rasmni kesish oynasi.
 *
 * Kadr qo'zg'almaydi, rasm uning ostida suriladi va kattalashadi. Kutubxona
 * yo'q (CLAUDE.md): matematika `lib/crop.ts` da, bu yerda faqat ko'rinish
 * va canvas'ga chizish.
 *
 * Zoom 1 dan kichik bo'la oladi: uzun tovarni kesib tashlamasdan kvadratga
 * sig'dirish uchun (atrofi bo'sh qoladi — PNG'da shaffof, JPEG'da oq).
 * Klaviatura ham to'liq ishlaydi: strelkalar suradi, `+` va `-` masshtab.
 */
export function ImageCropDialog({
  file,
  position,
  onDone,
  onSkip,
  onCancel,
  aspects = CROP_ASPECT_KEYS,
  frameLongSide = FRAME_LONG_SIDE,
}: ImageCropDialogProps): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const titleId = useId();
  const zoomId = useId();

  const panelRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ pointerId: number; start: Point; origin: Point } | null>(null);

  const [url, setUrl] = useState<string | null>(null);
  const [image, setImage] = useState<Size | null>(null);
  const [failed, setFailed] = useState(false);
  const [aspect, setAspect] = useState<CropAspect>(aspects[0] ?? 'square');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  // Ota har renderda yangi `onCancel` beradi; effekt uni ushlab turmasin,
  // aks holda har renderda fokus qaytib ketardi.
  const cancelRef = useRef(onCancel);
  useEffect(() => {
    cancelRef.current = onCancel;
  }, [onCancel]);

  const frame = useMemo(() => frameSize(aspect, frameLongSide), [aspect, frameLongSide]);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  // Oyna ochiq: Escape yopadi, sahifa siljimaydi, fokus qaytadi.
  useEffect(() => {
    const previous = document.activeElement;
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') cancelRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);

  const changeZoom = (next: number): void => {
    if (image === null) return;
    const clamped = clampZoom(next, image, frame);
    setZoom(clamped);
    setOffset((current) => clampOffset(current, image, frame, clamped));
  };

  const moveBy = (dx: number, dy: number): void => {
    if (image === null) return;
    setOffset((current) =>
      clampOffset({ x: current.x + dx, y: current.y + dy }, image, frame, zoom),
    );
  };

  // Sichqoncha g'ildiragi: `preventDefault` uchun passiv bo'lmagan tinglovchi kerak.
  useEffect(() => {
    const element = frameRef.current;
    if (element === null || image === null) return undefined;

    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const next = clampZoom(zoom * Math.exp(-event.deltaY * ZOOM_WHEEL_SPEED), image, frame);
      setZoom(next);
      setOffset((current) => clampOffset(current, image, frame, next));
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', onWheel);
    };
  }, [image, frame, zoom]);

  const chooseAspect = (next: CropAspect): void => {
    setAspect(next);
    // Kadr o'zgardi: eski zoom va siljish yangi kadrga mos kelmaydi.
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (image === null) return;
    if ('setPointerCapture' in event.currentTarget) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    dragRef.current = {
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      origin: offset,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || image === null || drag.pointerId !== event.pointerId) return;
    setOffset(
      clampOffset(
        {
          x: drag.origin.x + event.clientX - drag.start.x,
          y: drag.origin.y + event.clientY - drag.start.y,
        },
        image,
        frame,
        zoom,
      ),
    );
  };

  const endDrag = (): void => {
    dragRef.current = null;
  };

  const onFrameKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = event.shiftKey ? NUDGE_FAST : NUDGE;
    switch (event.key) {
      case 'ArrowLeft':
        moveBy(-step, 0);
        break;
      case 'ArrowRight':
        moveBy(step, 0);
        break;
      case 'ArrowUp':
        moveBy(0, -step);
        break;
      case 'ArrowDown':
        moveBy(0, step);
        break;
      case '+':
      case '=':
        changeZoom(zoom * ZOOM_KEY_FACTOR);
        break;
      case '-':
      case '_':
        changeZoom(zoom / ZOOM_KEY_FACTOR);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const apply = (): void => {
    const element = imageRef.current;
    if (element === null || image === null) return;

    setBusy(true);
    setExportFailed(false);

    const plan = cropPlan(image, frame, zoom, offset);
    const type = outputType(file.type);
    const canvas = document.createElement('canvas');
    canvas.width = plan.output.width;
    canvas.height = plan.output.height;

    const context = canvas.getContext('2d');
    if (context === null) {
      setBusy(false);
      setExportFailed(true);
      return;
    }
    // JPEG shaffoflikni bilmaydi: bo'sh joy qora bo'lib qolmasin.
    if (type === 'image/jpeg') {
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      element,
      plan.source.x,
      plan.source.y,
      plan.source.width,
      plan.source.height,
      plan.dest.x,
      plan.dest.y,
      plan.dest.width,
      plan.dest.height,
    );

    canvas.toBlob(
      (blob) => {
        if (blob === null) {
          setBusy(false);
          setExportFailed(true);
          return;
        }
        onDone(new File([blob], croppedFileName(file.name, type), { type }));
      },
      type,
      OUTPUT_QUALITY,
    );
  };

  const scale = image === null ? 1 : coverScale(image, frame) * zoom;
  const lowestZoom = image === null ? 1 : minZoom(image, frame);

  return (
    <div className="adm-modal">
      <div className="adm-modal__backdrop" aria-hidden="true" />
      <div
        ref={panelRef}
        className="adm-modal__panel adm-crop"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="adm-crop__header">
          <h2 id={titleId} className="adm-crop__title">
            {t.cropTitle}
          </h2>
          <span className="aps-muted adm-crop__file">
            {String(position.index)} / {String(position.total)} · {file.name}
          </span>
        </header>

        {failed && (
          <p className="aps-error" role="alert">
            {t.cropLoadFailed}
          </p>
        )}

        <div className="adm-crop__editor" hidden={failed}>
          <p className="aps-muted">{t.cropHint}</p>

          {aspects.length > 1 && (
            <div className="adm-crop__aspects" role="group" aria-label={t.cropAspectGroup}>
              {aspects.map((key) => (
                <Button
                  key={key}
                  size="sm"
                  variant={key === aspect ? 'primary' : 'secondary'}
                  aria-pressed={key === aspect}
                  onClick={() => {
                    chooseAspect(key);
                  }}
                >
                  {t[ASPECT_LABEL_KEYS[key]]}
                </Button>
              ))}
            </div>
          )}

          <div
            ref={frameRef}
            className="adm-crop__frame"
            role="group"
            aria-label={t.cropFrameLabel}
            tabIndex={0}
            style={{ width: frame.width, height: frame.height }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onFrameKeyDown}
          >
            {url !== null && (
              <img
                ref={imageRef}
                className="adm-crop__image"
                src={url}
                alt=""
                draggable={false}
                style={
                  image === null
                    ? { visibility: 'hidden' }
                    : {
                        width: image.width * scale,
                        height: image.height * scale,
                        transform: `translate(-50%, -50%) translate(${String(offset.x)}px, ${String(offset.y)}px)`,
                      }
                }
                onLoad={(event) => {
                  const { naturalWidth, naturalHeight } = event.currentTarget;
                  if (naturalWidth === 0 || naturalHeight === 0) setFailed(true);
                  else setImage({ width: naturalWidth, height: naturalHeight });
                }}
                onError={() => {
                  setFailed(true);
                }}
              />
            )}
            <span className="adm-crop__grid" aria-hidden="true" />
          </div>

          <div className="aps-field">
            <label className="aps-field__label" htmlFor={zoomId}>
              {t.cropZoom}
            </label>
            <input
              id={zoomId}
              className="adm-crop__zoom"
              type="range"
              min={lowestZoom}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              disabled={image === null}
              onChange={(event) => {
                changeZoom(Number(event.target.value));
              }}
            />
          </div>
        </div>

        {exportFailed && (
          <p className="aps-error" role="alert">
            {t.cropExportFailed}
          </p>
        )}

        <footer className="adm-crop__footer">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t.cropCancel}
          </Button>
          <Button variant="secondary" onClick={onSkip} disabled={busy}>
            {t.cropSkip}
          </Button>
          {!failed && (
            <Button onClick={apply} disabled={busy || image === null}>
              {t.cropApply}
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}
